import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from 'src/components/Stack';
import { useSession } from 'src/contexts/SessionContext';
import i18n from 'src/i18n';
import { loadStripe } from 'src/models/cart/utils';
import { fetchSetupIntent } from 'src/models/payment/payment';
import { PaymentMethodType } from 'src/models/payment-methods/_types';
import { getPaymentMethodsForCurrency } from 'src/models/payment-methods/utils';
import { getUserDisplayName, isHeadTrainer } from 'src/models/user/utils';

import { Button } from './Button';

import type { StripePaymentElementOptions } from '@stripe/stripe-js';
import type { User } from 'src/models/user/_types';

export interface PaymentMethodFormProps {
  user?: User;
  handlePaymentMethodAdded: (result: any) => void;
  paymentMethodTypes?: PaymentMethodType[];
  billingDetails?: 'auto' | 'never';
  buttonText?: string;
  onLoaded?: () => void;
}

export const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
  user,
  handlePaymentMethodAdded,
  paymentMethodTypes,
  billingDetails = 'never',
  buttonText,
  onLoaded,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'paymentMethod' });
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const { session, user: currentUser } = useSession();
  const usePlatformStripeAccount =
    currentUser &&
    user &&
    currentUser.id === user.id &&
    isHeadTrainer(currentUser);

  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    if (session && !stripePromise) {
      setStripePromise(loadStripe(session, usePlatformStripeAccount));
    }
  }, [session, stripePromise, usePlatformStripeAccount]);

  useEffect(() => {
    if (!user || !stripePromise || clientSecret) {
      return;
    }
    // Function to fetch the client secret
    const fetchClientSecret = async () => {
      // Use provided paymentMethodTypes, fall back to currency-based types
      const methodTypes =
        paymentMethodTypes ||
        getPaymentMethodsForCurrency(session?.defaultCurrency, session?.country || 'US');

      fetchSetupIntent({
        userId: user.id,
        paymentMethodTypes: methodTypes,
        forClient: !usePlatformStripeAccount,
      }).then((data) => {
        setClientSecret(data.clientSecret);
        onLoaded?.();
      });
    };

    fetchClientSecret();
  }, [
    user,
    stripePromise,
    session?.defaultCurrency,
    session?.country,
    clientSecret,
    usePlatformStripeAccount,
    paymentMethodTypes,
    onLoaded,
  ]);

  if (!clientSecret) {
    return <div>{t('loading')}</div>;
  }

  const options = {
    clientSecret,
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm
        user={user}
        handlePaymentMethodAdded={(result) => handlePaymentMethodAdded(result)}
        billingDetails={billingDetails}
        buttonText={buttonText}
      />
    </Elements>
  );
};

interface CheckoutFormProps {
  user?: User;
  handlePaymentMethodAdded: (result: any) => void;
  billingDetails?: 'auto' | 'never';
  buttonText?: string;
}
const CheckoutForm: React.FC<CheckoutFormProps> = ({
  user,
  handlePaymentMethodAdded,
  billingDetails = 'never',
  buttonText,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'paymentMethod' });
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const result = await stripe.confirmSetup({
      elements,
      confirmParams: {
        payment_method_data: {
          billing_details: {
            name: getUserDisplayName(user),
            email: user?.email,
          },
        },
        expand: ['payment_method'],
      },
      redirect: 'if_required',
    });

    if (result.error) {
      // error
    } else {
      handlePaymentMethodAdded(result);
    }
  };

  const paymentElementOptions: StripePaymentElementOptions = {
    fields: {
      billingDetails: {
        name: billingDetails,
        email: billingDetails,
      },
    },
    layout: {
      type: 'tabs',
      defaultCollapsed: false,
    },
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={1}>
        <PaymentElement options={paymentElementOptions} />
        <Button variant="prominent" disabled={!stripe} type="submit">
          {buttonText || t('submit')}
        </Button>
      </Stack>
    </form>
  );
};

export const getBacsRedirectUrl = async (
  userId: User['id'],
  onWindowClose?: (setupIntent: string) => void,
  preOpenedWindow?: Window | null
) => {
  // iOS/iPadOS Safari blocks window.open calls that happen after an `await`
  // because they're no longer associated with the user gesture. Callers should
  // open a blank window synchronously inside the click handler and pass it in
  // here; we then navigate it once we have the redirect URL.
  let targetWindow: Window | null = preOpenedWindow ?? null;

  try {
    const response = await fetchSetupIntent({
      userId,
      paymentMethodTypes: ['bacs_debit'],
    });

    if (!response.url) {
      throw new Error(i18n.t('paymentMethod.noRedirectUrl'));
    }

    if (targetWindow) {
      targetWindow.location.href = response.url;
    } else {
      targetWindow = window.open(response.url, '_blank');
    }

    if (targetWindow && onWindowClose) {
      const newWindow = targetWindow;
      const timer = setInterval(() => {
        if (newWindow.closed) {
          clearInterval(timer);
          onWindowClose(response.setupIntent);
        }
      }, 500);
    }
  } catch (error) {
    if (targetWindow && !targetWindow.closed) {
      targetWindow.close();
    }
    throw error;
  }
};
